import { useEffect, useState } from "react";
import closeButton from '../../assets/images/close.png';
import { useNavigate } from "react-router-dom";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import { formatCurrency } from "../../utility/formatCurrency";
import { createNegotiation, getNegotiationList, updateNegotiation } from "../../services/AppService";
import AddNegotiationForm from "../../components/dashboard/Prceedings/NegotiationProcess/AddNegotiationForm";
import { useTranslation } from "react-i18next";

const NegotiationProcess = () => {
    const {t}=useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [negotiations, setNegotiations] = useState([]);
    const [editingItem, setEditingItem] = useState(null);

    // Pagination & Search
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const [confirmModal, setConfirmModal] = useState({ open: false, formData: null });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const showSuccess = (msg) => setMessageModal({ open: true, type: "success", title: "Success", message: msg });
    const showError = (msg) => setMessageModal({ open: true, type: "error", title: "Error", message: msg });

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchNegotiations();
    }, [page, size, debouncedSearch]);

    const fetchNegotiations = async () => {
        try {
            setLoading(true);
            const response = await getNegotiationList(debouncedSearch, page, size);
            if (response) {
                setNegotiations(response.content || []);
                setTotalPages(response.totalPages || 0);
                setTotalElements(response.totalElements || 0);
            }
        } catch (error) {
            showError("Failed to load negotiation details");
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = (formData) => {
        setIsModalOpen(false);
        setConfirmModal({ open: true, formData: formData });
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            if (editingItem) {
                await updateNegotiation(editingItem.id, formData);
                showSuccess("Negotiation updated successfully");
            } else {
                await createNegotiation(formData);
                showSuccess("Negotiation added successfully");
            }
            fetchNegotiations();
            setConfirmModal({ open: false, formData: null });
            setEditingItem(null);
        } catch (error) {
            showError(error.response?.data?.message || "Internal Server Error");
        }
    };

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">{t("pageTitles.negotiationProcessList")}</h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
            </div>

            <div className="text-end mb-2">
                <button className="btnAdd" onClick={() => { setEditingItem(null); setIsModalOpen(true); }}>
                    {t('common.addNew')}
                </button>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t('common.show')}:</label>
                    <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} className="border rounded px-2 py-1 text-sm outline-none">
                        {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label className="text-sm">{t('common.entries')}</label>
                </div>
                <input type="text" placeholder={t('tables.negotiationProcess.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="border rounded px-3 py-1 text-sm outline-none w-full sm:w-64" />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full border text-sm ctable rounded-lg">
                    <thead>
                        <tr className="bg-[#a0adf1] text-white">
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.workName')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.negotiationProcess.tenderId')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.negotiationProcess.bidderName')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.negotiationProcess.tenderValue')}</th>
                            <th className="px-3 py-2 border-b border-black">Above Below</th>
                            <th className="px-3 py-2 border-b border-black">Bid Amount</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.edit')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center py-4">{t('tables.negotiationProcess.loadingNegotiationProcess')}</td></tr>
                        ) : negotiations.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-4 text-gray-500">{t('tables.negotiationProcess.noNegotiationProcess')}</td></tr>
                        ) : (
                            negotiations.map((item, idx) => (
                                <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}>
                                    <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                    <td className="px-3 py-2">{item.workTitle}</td>
                                    <td className="px-3 py-2">{item.tenderId}</td>
                                    <td className="px-3 py-2">{item.bidderName}</td>
                                    <td className="px-3 py-2">{formatCurrency(item.tenderValue)}</td>
                                    <td className="px-3 py-2">{item.aboveBelow?.toFixed(1)}%</td>
                                    <td className="px-3 py-2">{formatCurrency(item.bidAmount)}</td>
                                    <td className="px-3 py-2 text-center">
                                        <button className="px-3 py-1 btnImgEdit rounded text-xs cursor-pointer" onClick={() => { setEditingItem(item); setIsModalOpen(true); }}></button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-between items-center mt-4 text-sm">
                <p>{t('common.showing')} {page * size + 1} {t('common.to')} {page * size + negotiations.length} {t('common.of')} {totalElements} {t('common.entries')}</p>
                <div className="flex gap-2">
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>{t('common.previous')}</button>
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={page === totalPages - 1}>{t('common.next')}</button>
                </div>
            </div>

            <AddNegotiationForm
                onSubmit={handleFormSubmit}
                editData={editingItem}
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingItem(null); }}
            />

            <ConfirmActionModal
                open={confirmModal.open}
                title={editingItem ? "Confirm Update" : "Confirm Addition"}
                message={`Are you sure you want to ${editingItem ? "update" : "save"} these negotiation details?`}
                onConfirm={handleConfirmSave}
                onClose={() => setConfirmModal({ open: false, formData: null })}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />
        </div>
    );
};

export default NegotiationProcess;